class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      const upgradeButton = event.target.closest('[data-cart-bundle-upgrade]');
      if (upgradeButton) this.upgradeBundle(upgradeButton);
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  async upgradeBundle(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const originalLabel = button.textContent;
    const sections = this.getSectionsToRender().map((section) => section.id);
    let addedState;

    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Updating…';

    try {
      const addResponse = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.targetVariantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      addedState = await addResponse.json();
      if (!addResponse.ok || addedState.status) {
        throw new Error(addedState.description || window.cartStrings.error);
      }

      const changeResponse = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: button.dataset.sourceKey,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const changedState = await changeResponse.json();
      if (!changeResponse.ok || changedState.errors) {
        throw new Error(changedState.description || changedState.errors || window.cartStrings.error);
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-bundle-upgrade' });
      this.renderContents(changedState);
    } catch (error) {
      if (addedState?.sections) this.renderContents(addedState);
      const errorRegion = document.getElementById('CartDrawer-CartErrors');
      if (errorRegion) errorRegion.textContent = error.message;
      button.removeAttribute('aria-disabled');
      button.textContent = originalLabel;
    }
  }

  setHeaderCartIconAccessibility() {
    // Handle both mobile and desktop cart icons with a single class selector
    const cartLinks = document.querySelectorAll('.js-cart-icon');
    cartLinks.forEach(cartLink => {
      if (!cartLink) return;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink)
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (section.id === 'cart-icon-bubble') {
        // Update all cart icons using the shared class
        const innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        document.querySelectorAll('.js-cart-icon').forEach(icon => {
          if (icon) icon.innerHTML = innerHTML;
        });
      } else {
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (sectionElement) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
